import { useState, useEffect } from "react";

interface Review {
  id: string;
  restaurantId: string;
  author: string;
  rating: number;
  comment: string;
  date: string;
  helpful: number;
  helpedBy: string[];
  hidden?: boolean;
}

const STORAGE_KEY = "restaurantReviews";

const StarDisplay = ({ value }: { value: number }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((star) => (
      <i
        key={star}
        className={`text-xs ${star <= value ? "ri-star-fill text-amber-400" : "ri-star-line text-stone-300"}`}
      ></i>
    ))}
  </div>
);

const ReviewsModeration = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [filter, setFilter] = useState<"all" | "visible" | "hidden" | "low">("all");
  const [sortBy, setSortBy] = useState<"newest" | "oldest" | "lowest" | "highest">("newest");
  const [search, setSearch] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: "success" | "error" } | null>(null);

  useEffect(() => {
    const all: Review[] = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
    setReviews(all);
  }, []);

  const saveAll = (updated: Review[]) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    setReviews(updated);
  };

  const showToast = (msg: string, type: "success" | "error") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const toggleHide = (id: string) => {
    const updated = reviews.map((r) =>
      r.id === id ? { ...r, hidden: !r.hidden } : r
    );
    saveAll(updated);
    const rev = updated.find((r) => r.id === id);
    showToast(rev?.hidden ? "Yorum gizlendi." : "Yorum tekrar görünür yapıldı.", "success");
  };

  const deleteReview = (id: string) => {
    const updated = reviews.filter((r) => r.id !== id);
    saveAll(updated);
    setDeleteConfirm(null);
    showToast("Yorum silindi.", "error");
  };

  const filtered = reviews
    .filter((r) => {
      if (filter === "visible") return !r.hidden;
      if (filter === "hidden") return r.hidden;
      if (filter === "low") return r.rating <= 2;
      return true;
    })
    .filter((r) => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (
        r.author.toLowerCase().includes(q) ||
        r.comment.toLowerCase().includes(q) ||
        r.restaurantId.toLowerCase().includes(q)
      );
    })
    .sort((a, b) => {
      if (sortBy === "newest") return new Date(b.date).getTime() - new Date(a.date).getTime();
      if (sortBy === "oldest") return new Date(a.date).getTime() - new Date(b.date).getTime();
      if (sortBy === "lowest") return a.rating - b.rating;
      if (sortBy === "highest") return b.rating - a.rating;
      return 0;
    });

  const totalCount = reviews.length;
  const hiddenCount = reviews.filter((r) => r.hidden).length;
  const lowCount = reviews.filter((r) => r.rating <= 2).length;
  const avgRating = reviews.length > 0
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : "—";

  return (
    <div className="relative">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-20 right-4 z-50 px-4 py-3 rounded-xl text-sm font-medium flex items-center gap-2
          ${toast.type === "success" ? "bg-emerald-500 text-white" : "bg-rose-500 text-white"}`}>
          <i className={toast.type === "success" ? "ri-checkbox-circle-line" : "ri-delete-bin-line"}></i>
          {toast.msg}
        </div>
      )}

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
            <div className="w-12 h-12 flex items-center justify-center bg-rose-100 rounded-full mx-auto mb-4">
              <i className="ri-delete-bin-line text-rose-600 text-xl"></i>
            </div>
            <h3 className="text-base font-bold text-stone-900 text-center mb-2">Yorumu silmek istiyor musunuz?</h3>
            <p className="text-stone-500 text-sm text-center mb-5">Bu işlem geri alınamaz.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 border border-stone-200 text-stone-600 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-stone-50 transition-colors">İptal</button>
              <button onClick={() => deleteReview(deleteConfirm)} className="flex-1 py-2.5 bg-rose-500 text-white rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-rose-600 transition-colors">Sil</button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
          Yorum Moderasyonu
        </h2>
        <p className="text-stone-400 text-xs mt-0.5">Ziyaretçi yorumlarını görüntüleyin, gizleyin veya silin</p>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <div className="bg-white rounded-xl border border-stone-100 p-4 text-center">
          <p className="text-2xl font-bold text-stone-900">{totalCount}</p>
          <p className="text-xs text-stone-400 mt-0.5">Toplam Yorum</p>
        </div>
        <div className="bg-white rounded-xl border border-stone-100 p-4 text-center">
          <p className="text-2xl font-bold text-amber-500">{avgRating}</p>
          <p className="text-xs text-stone-400 mt-0.5">Ortalama Puan</p>
        </div>
        <div className="bg-white rounded-xl border border-stone-100 p-4 text-center">
          <p className="text-2xl font-bold text-stone-400">{hiddenCount}</p>
          <p className="text-xs text-stone-400 mt-0.5">Gizli Yorum</p>
        </div>
        <div className="bg-white rounded-xl border border-stone-100 p-4 text-center">
          <p className="text-2xl font-bold text-rose-500">{lowCount}</p>
          <p className="text-xs text-stone-400 mt-0.5">Düşük Puanlı (1-2)</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex items-center bg-stone-100 rounded-full p-1 gap-1">
          {([
            { key: "all", label: `Tümü (${totalCount})` },
            { key: "visible", label: `Görünür (${totalCount - hiddenCount})` },
            { key: "hidden", label: `Gizli (${hiddenCount})` },
            { key: "low", label: `Düşük Puan (${lowCount})` },
          ] as const).map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              className={`text-xs px-3 py-1.5 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap
                ${filter === key ? "bg-white text-stone-800" : "text-stone-500 hover:text-stone-700"}`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2 ml-auto flex-wrap">
          {/* Search */}
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm"></i>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Yazar, yorum veya restoran ara..."
              className="pl-8 pr-3 py-2 text-xs border border-stone-200 rounded-lg outline-none focus:border-amber-400 transition-colors w-56"
            />
          </div>
          {/* Sort */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
            className="text-xs border border-stone-200 rounded-lg px-3 py-2 text-stone-600 outline-none cursor-pointer focus:border-amber-400 transition-colors"
          >
            <option value="newest">En Yeni</option>
            <option value="oldest">En Eski</option>
            <option value="lowest">En Düşük Puan</option>
            <option value="highest">En Yüksek Puan</option>
          </select>
        </div>
      </div>

      {/* Reviews List */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-stone-100">
          <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 bg-stone-100 rounded-full">
            <i className="ri-chat-3-line text-stone-400 text-2xl"></i>
          </div>
          <p className="text-stone-500 font-medium text-sm">
            {totalCount === 0 ? "Henüz yorum yapılmamış" : "Bu filtreye uygun yorum yok"}
          </p>
          <p className="text-stone-400 text-xs mt-1">
            {totalCount === 0
              ? "Ziyaretçiler restoran detay sayfalarından yorum yapabilir"
              : "Farklı bir filtre deneyin"}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((review) => (
            <div
              key={review.id}
              className={`bg-white rounded-xl border p-4 transition-all
                ${review.hidden ? "border-stone-200 opacity-60" : review.rating <= 2 ? "border-rose-200" : "border-stone-100"}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  {/* Avatar */}
                  <div className={`w-9 h-9 flex items-center justify-center rounded-full flex-shrink-0
                    ${review.hidden ? "bg-stone-100" : "bg-amber-100"}`}>
                    <span className={`font-bold text-sm ${review.hidden ? "text-stone-400" : "text-amber-700"}`}>
                      {review.author.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="text-sm font-semibold text-stone-900">{review.author}</span>
                      <StarDisplay value={review.rating} />
                      {review.hidden && (
                        <span className="text-xs bg-stone-100 text-stone-500 px-2 py-0.5 rounded-full flex items-center gap-1">
                          <i className="ri-eye-off-line text-xs"></i> Gizli
                        </span>
                      )}
                      {review.rating <= 2 && !review.hidden && (
                        <span className="text-xs bg-rose-100 text-rose-600 px-2 py-0.5 rounded-full">
                          Düşük Puan
                        </span>
                      )}
                    </div>
                    {/* Restaurant ID */}
                    <div className="flex items-center gap-1 mb-1.5">
                      <i className="ri-store-2-line text-stone-400 text-xs"></i>
                      <span className="text-xs text-stone-400 truncate">{review.restaurantId}</span>
                    </div>
                    <p className="text-sm text-stone-600 leading-relaxed line-clamp-3">{review.comment}</p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-stone-400">
                      <span>{new Date(review.date).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })}</span>
                      {review.helpful > 0 && (
                        <span className="flex items-center gap-1">
                          <i className="ri-thumb-up-line"></i> {review.helpful} faydalı
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => toggleHide(review.id)}
                    title={review.hidden ? "Görünür yap" : "Gizle"}
                    className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors cursor-pointer
                      ${review.hidden
                        ? "text-emerald-500 hover:bg-emerald-50"
                        : "text-stone-400 hover:text-stone-600 hover:bg-stone-100"}`}
                  >
                    <i className={review.hidden ? "ri-eye-line" : "ri-eye-off-line"} style={{ fontSize: 14 }}></i>
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(review.id)}
                    title="Sil"
                    className="w-8 h-8 flex items-center justify-center text-stone-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                  >
                    <i className="ri-delete-bin-line" style={{ fontSize: 14 }}></i>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReviewsModeration;
